import { MotionConfig } from 'motion/react';
import { Suspense, lazy, type ReactNode } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Route, Routes, useParams } from 'react-router';
import { readDeviceKey } from './api/api';
import { RootBoundary } from './components/ErrorBoundary';
import { FullscreenMessage } from './components/FullscreenMessage';
import { SHELL } from './i18n/it';
import type { ScreenId } from './protocol/types';
import { DisplayProvider } from './realtime/DisplayProvider';
import { useConnection } from './realtime/hooks';

// Ogni schermo è un pezzo a parte: il tablet non scarica i grafici del Monitor A.
const MonitorA = lazy(() =>
  import('./screens/MonitorA/MonitorA').then((m) => ({ default: m.MonitorA })),
);
const MonitorB = lazy(() =>
  import('./screens/MonitorB/MonitorB').then((m) => ({ default: m.MonitorB })),
);
const Tablet = lazy(() => import('./screens/Tablet/Tablet').then((m) => ({ default: m.Tablet })));

/** Una rotta per schermo: /display/monitor-a, /display/monitor-b, /display/tablet (chiave in #k=…). */
const SCREENS: Record<string, { id: ScreenId; element: ReactNode }> = {
  'monitor-a': { id: 'monitor-a', element: <MonitorA /> },
  'monitor-b': { id: 'monitor-b', element: <MonitorB /> },
  tablet: { id: 'tablet', element: <Tablet /> },
};

function AuthGate({ children }: { children: ReactNode }) {
  const { status } = useConnection();
  if (status === 'unauthorized') return <FullscreenMessage {...SHELL.unauthorized} />;
  return <>{children}</>;
}

function ScreenRoute() {
  const { screen } = useParams();
  const entry = screen ? SCREENS[screen] : undefined;
  if (!entry) return <FullscreenMessage {...SHELL.notFound} />;
  const deviceKey = readDeviceKey();
  if (!deviceKey) {
    const { title, detail } = SHELL.notConfigured;
    return (
      <FullscreenMessage
        title={title}
        detail={import.meta.env.DEV ? `${detail} ${SHELL.devHint}` : detail}
      />
    );
  }
  return (
    <DisplayProvider screen={entry.id} deviceKey={deviceKey}>
      <AuthGate>
        <RootBoundary>
          <Suspense fallback={null}>{entry.element}</Suspense>
        </RootBoundary>
      </AuthGate>
    </DisplayProvider>
  );
}

export function App() {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <Routes>
          <Route path="/display/:screen" element={<ScreenRoute />} />
          <Route path="*" element={<FullscreenMessage {...SHELL.notFound} />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'var(--tiw-tablet-card)',
            color: 'var(--tiw-tablet-text)',
            border: '1px solid var(--tiw-tablet-line)',
          },
        }}
      />
    </MotionConfig>
  );
}
